import { useEffect } from 'react'
import { ControlPosition, useMap } from '@vis.gl/react-google-maps'
import { useMediaQuery } from '@/hooks/use-media-query'

const MOBILE_STACK = ControlPosition.INLINE_END_BLOCK_END

/**
 * Configures Google Maps built-in controls.
 *
 * Per ControlPosition docs: controls sharing a slot flow toward the middle;
 * **first added = closest to the map edge**. For INLINE_END_BLOCK_END
 * (RIGHT_BOTTOM), first → bottom, later → higher up.
 *
 * Mobile desired visual top→bottom: Fullscreen → Street View → Camera.
 * Add order: Camera, then Street View, then Fullscreen (all native).
 *
 * Desktop: camera left, street view bottom-right, fullscreen top-right.
 *
 * @see https://developers.google.com/maps/documentation/javascript/reference/control#ControlPosition
 */
export function MapFullscreenControl() {
  const map = useMap()
  const isMobile = useMediaQuery('(max-width: 767px)')

  useEffect(() => {
    if (!map) return
    let cancelled = false

    if (!isMobile) {
      map.setOptions({
        zoomControl: false,
        rotateControl: false,
        cameraControl: true,
        cameraControlOptions: {
          position: ControlPosition.INLINE_START_BLOCK_END,
        },
        streetViewControl: true,
        streetViewControlOptions: {
          position: ControlPosition.INLINE_END_BLOCK_END,
        },
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: ControlPosition.INLINE_END_BLOCK_START,
        },
      })
      return
    }

    // Clear, then re-add in controlled order so Google’s layout matches the stack.
    map.setOptions({
      zoomControl: false,
      rotateControl: false,
      cameraControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    })

    requestAnimationFrame(() => {
      if (cancelled) return
      // 1) Camera → bottom
      map.setOptions({
        cameraControl: true,
        cameraControlOptions: { position: MOBILE_STACK },
      })
      requestAnimationFrame(() => {
        if (cancelled) return
        // 2) Street View → middle
        map.setOptions({
          streetViewControl: true,
          streetViewControlOptions: { position: MOBILE_STACK },
        })
        requestAnimationFrame(() => {
          if (cancelled) return
          // 3) Fullscreen → top
          map.setOptions({
            fullscreenControl: true,
            fullscreenControlOptions: { position: MOBILE_STACK },
          })
        })
      })
    })

    return () => {
      cancelled = true
    }
  }, [map, isMobile])

  return null
}
